import { useCallback, useEffect, useState } from "react";
import { listDraftProfiles, putDraftProfile, type SavedDraftProfile } from "../../db";
import { PROFILE_BASE } from "../../profiles";
import { resolveDraftProfile, type ResolvedDraftProfile } from "../../profiles/resolve-draft";

/**
 * The locally measured draft profiles, resolved and ready to govern a run.
 *
 * Kept out of `src/profiles/index.ts` deliberately. That registry is a
 * synchronous module constant, and node tooling imports the same tree with no
 * IndexedDB to read — teaching it about drafts would either make it async for
 * everyone or give the tooling a second, divergent code path. A draft is a
 * browser-local thing, so it is assembled here, in the feature that creates it.
 *
 * A draft resolves the same way a committed profile does apart from the merge
 * base, and it carries the same policy hash, so everything downstream treats it
 * identically — except the export gate, which blocks the recovered artifact on
 * the draft stamp.
 */
export type DraftProfilesState = {
  /** Stored drafts, resolved. Empty until the read lands, and on a failing IndexedDB. */
  drafts: ResolvedDraftProfile[];
  /** The raw stored rows, for showing the evidence a draft was saved with. */
  rows: SavedDraftProfile[];
  /** True until the first read settles. */
  loading: boolean;
  /** Persist a draft and add it to the list. Rejects if the write fails. */
  saveDraft: (row: SavedDraftProfile) => Promise<ResolvedDraftProfile>;
};

export function useDraftProfiles(): DraftProfilesState {
  const [rows, setRows] = useState<SavedDraftProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void listDraftProfiles().then((stored) => {
      if (cancelled) return;
      setRows(stored);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveDraft = useCallback(async (row: SavedDraftProfile) => {
    // Write first: a draft the person believes was saved but was not would send
    // them back to another source's policy on the next upload, unaware.
    await putDraftProfile(row);
    setRows((current) => [...current.filter((entry) => entry.draftId !== row.draftId), row]);
    return toResolved(row);
  }, []);

  return { drafts: rows.map(toResolved), rows, loading, saveDraft };
}

function toResolved(row: SavedDraftProfile): ResolvedDraftProfile {
  return resolveDraftProfile(PROFILE_BASE!, row.delta, {
    measuredAt: row.createdAt,
    baselineFileName: row.baselineFileName,
    latestFileName: row.latestFileName
  });
}
